import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import AddImage from "../Components/AddImage";
import DepartmentBattleDateOption from "../Components/DepartmentBattleDateOption";
import InputContent from "../Components/InputContent";
import InputTitle from "../Components/InputTitle";
import RadioButton from "../Components/RadioButton";
import ToggleButton from "../Components/ToggleButton";
import EventExampleImage from "../Images/imageEx_event.svg";
import EventExampleScreen from "../Images/screenEx_event.svg";
import { ReactComponent as RadioButtonActive } from "../Images/radiobutton_active.svg";
import { ReactComponent as RadioButtonDefault } from "../Images/radiobutton_default.svg";
import { ReactComponent as IconInfo } from "../Images/icon_info.svg";
import Layout from "../Layout/Layout";
import ImageUpload from "../Modals/ImageUpload";
import AlarmApply from "../Modals/AlarmApply";
import AlarmInputCheck from "../Modals/AlarmInputCheck";
import ImageEx from "../Modals/ImageEx";
import "../Styles/component.css";
import "../Styles/fonts.css";
import "../Styles/global.css";
import "../Styles/lists.css";
import {
  requestCreateDepartmentBattle,
  requestDepartmentBattleDetail,
  requestUpdateDepartmentBattle,
} from "./DepartmentBattle.api";
import { showToast } from "../Utils/variables";

const parseDateValue = (dateString) => {
  if (!dateString) {
    return getTodayDate();
  }

  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const getTodayDate = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
};

const getDefaultEndDate = () => {
  const date = new Date();
  date.setDate(date.getDate() + 30);
  date.setHours(0, 0, 0, 0);
  return date;
};

const formatDateValue = (value) => {
  if (!value) {
    return "";
  }

  if (value instanceof Date) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  return String(value).slice(0, 10);
};

const NO_DATA_STEP_TYPE = {
  COMPANY_AVG: 1,
  MANUAL: 2,
};

const DepartmentBattleApplyContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEditing = location.pathname.includes("/edit/");
  const isDetailMode = location.pathname.includes("/detail/");
  const [loadedEntry, setLoadedEntry] = useState(null);
  const [clickImageEx, setClickImageEx] = useState(false);
  const [clickUploadImage, setClickUploadImage] = useState(false);
  const [clickApply, setClickApply] = useState(false);
  const [inputCheckFail, setInputCheckFail] = useState(false);
  const lastDepartmentBattleDetailRequestKey = useRef(null);
  const [form, setForm] = useState({
    isVisible: true,
    image: "",
    startDate: getTodayDate(),
    endDate: getDefaultEndDate(),
    nation: 0,
    title: "",
    description: "",
    targetRate: null,
    noDataStepType: NO_DATA_STEP_TYPE.COMPANY_AVG,
    noDataStepValue: "",
  });

  const comparisonToday = getTodayDate();
  const editStartDate = loadedEntry
    ? parseDateValue(loadedEntry.startDate)
    : form.startDate;
  const editEndDate = loadedEntry ? parseDateValue(loadedEntry.endDate) : form.endDate;
  const isOngoingEdit =
    isEditing &&
    editStartDate instanceof Date &&
    editEndDate instanceof Date &&
    editStartDate.getTime() <= comparisonToday.getTime() &&
    editEndDate.getTime() >= comparisonToday.getTime();

  const applyModalTitle = !isEditing
    ? "부서별 대항전을 등록하시겠습니까?"
    : isOngoingEdit
    ? "진행 중인 이벤트를 수정하시겠습니까?"
    : "부서별 대항전을 수정하시겠습니까?";

  const applyModalSubtitle = !isEditing
    ? "등록 후 부서별 대항전 관리 페이지에서 수정할 수 있습니다."
    : isOngoingEdit
    ? "이벤트 설정을 변경하면 참가자에게 혼란이 생길 수 있으며,\n변경된 내용은 즉시 반영됩니다."
    : "변경된 내용은 즉시 반영됩니다.";

  useEffect(() => {
    if (!id) {
      return;
    }

    const requestKey = JSON.stringify({ id, isDetailMode });

    if (lastDepartmentBattleDetailRequestKey.current === requestKey) {
      return;
    }

    lastDepartmentBattleDetailRequestKey.current = requestKey;

    const getDepartmentBattleData = async () => {
      try {
        const response = await requestDepartmentBattleDetail({
          seasonDepartmentId: id,
          navigate,
        });

        if (response.code !== 200) {
          showToast(
            isDetailMode
              ? "상세 조회할 부서별 대항전 데이터를 찾을 수 없습니다."
              : "수정할 부서별 대항전 데이터를 찾을 수 없습니다.",
            "error"
          );
          navigate("/departmentBattle");
          return;
        }

        const entry = response.data;

        setLoadedEntry(entry);
        setForm({
          isVisible: entry.isVisible,
          image: entry.imageUrl,
          startDate: parseDateValue(entry.startDate),
          endDate: parseDateValue(entry.endDate),
          nation: entry.nation,
          title: entry.title,
          description: entry.description,
          targetRate: entry.targetRate,
          noDataStepType: entry.noDataStepType,
          noDataStepValue: entry.noDataStepValue,
        });

      } catch (error) {
        console.log("부서별 대항전 상세 가져오기 중 에러:", error);
        throw error;
      }
    };

    getDepartmentBattleData();
  }, [id, isDetailMode, navigate]);

  const handleToggleVisible = () => {
    if (isDetailMode) {
      return;
    }

    setForm((prev) => ({ ...prev, isVisible: !prev.isVisible }));
  };

  const handleImageUpload = (event) => {
    if (isDetailMode) {
      return;
    }

    setForm((prev) => ({ ...prev, image: event }));
  };

  const pageTitle = isDetailMode
    ? "부서별 대항전 상세"
    : `부서별 대항전 ${isEditing ? "수정" : "등록"}`;
  const topNoticeText = isDetailMode
    ? "*상세 페이지에서는 대회 정보를 확인할 수 있습니다."
    : "*표시는 필수 입력 항목입니다.";

  const handleClickX = () => {
    setClickImageEx(false);
    setClickUploadImage(false);
    setClickApply(false);
    setInputCheckFail(false);
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      setInputCheckFail(true);
      return;
    }

    if (!form.description.trim()) {
      setInputCheckFail(true);
      return;
    }

    if (form.startDate.getTime() > form.endDate.getTime()) {
      showToast("시작일은 종료일보다 늦을 수 없습니다.", "error");
      return;
    }

    const hasImage = Array.isArray(form.image)
      ? form.image.length > 0
      : Boolean(form.image);

    if (!hasImage) {
      setInputCheckFail(true);
      return;
    }

    if (form.targetRate === null) {
      setInputCheckFail(true);
      return;
    }

    if (
      form.noDataStepType === NO_DATA_STEP_TYPE.MANUAL &&
      (!form.noDataStepValue ||
        !Number.isInteger(Number(form.noDataStepValue)) ||
        Number(form.noDataStepValue) <= 0)
    ) {
      setInputCheckFail(true);
      return;
    }

    try {
      const response = isEditing
        ? await requestUpdateDepartmentBattle({
            seasonDepartmentId: loadedEntry?.seasonDepartmentId ?? id,
            form,
            navigate,
          })
        : await requestCreateDepartmentBattle({
            form,
            navigate,
          });

      if (response.code === 200) {
        navigate("/departmentBattle", {
          state: {
            toast_enable: true,
            toast_message: isEditing
              ? "부서별 대항전이 수정되었습니다."
              : "부서별 대항전이 등록되었습니다.",
          },
        });
        return;
      }

      showToast(response.message ?? "저장 중 오류가 발생했습니다.", "error");
    } catch (error) {
      console.log("부서별 대항전 저장 중 에러:", error);
      throw error;
    }
  };

  return (
    <div>
      {clickImageEx && (
        <ImageEx
          title="부서별 대항전 이미지 예시"
          image={
            <img
              src={EventExampleImage}
              alt="부서별 대항전 이미지 예시"
              style={{ width: "100%", display: "block" }}
            />
          }
          screen={
            <img
              src={EventExampleScreen}
              alt="부서별 대항전 앱 적용 화면 예시"
              style={{ width: "100%", display: "block" }}
            />
          }
          handleClickX={handleClickX}
        />
      )}
      {clickUploadImage && (
        <ImageUpload
          title="대회이미지 업로드"
          info="대회 이미지는 1000x339px만 등록 가능합니다"
          handleClickX={handleClickX}
          pageName="DepartmentBattleApply"
          imageUpload={handleImageUpload}
          previewImg={form.image}
          requiredDimensions={{ width: 1000, height: 339 }}
        />
      )}
      {clickApply && !isDetailMode && (
        <AlarmApply
          title={applyModalTitle}
          subtitle={applyModalSubtitle}
          handleApply={handleSubmit}
          handleClickX={handleClickX}
        />
      )}
      {inputCheckFail && <AlarmInputCheck handleClickX={handleClickX} />}
      <ApplyBox>
        <div className="title-in-box">{pageTitle}</div>
        <div className="hrz-wrapper">
          <div
            className="text-for-notice"
            style={{ marginLeft: "auto", marginTop: "10px" }}
          >
            {topNoticeText}
          </div>
        </div>

        <VisibilityBox>
          <VisibilityContent>
            <VisibilityHeader>
              <p className="bold-text">부서별 대항전 앱 UI 노출 여부 설정</p>
              <div style={{ flexShrink: 0 }}>
                <ToggleButton
                  handleClickToggle={handleToggleVisible}
                  setValue={form.isVisible}
                  disabled={isDetailMode}
                />
              </div>
            </VisibilityHeader>
            <p
              className="text-for-notice"
              style={{ marginTop: 0, lineHeight: 1.6, wordBreak: "keep-all" }}
            >
              활성화 시, 앱 내 기업모드 홈에서 대회 시작 5일 전부터 부서별
              대항전 탭이 자동 노출됩니다. 다음 대회가 없을 경우, 종료
              <br />
              7일 후 해당 탭은 자동으로 숨김 처리됩니다.
            </p>
          </VisibilityContent>
        </VisibilityBox>

        <AddImage
          title="대회이미지"
          isRequired
          noticeText="대회 이미지는 1000x339px만 등록 가능합니다"
          handleClickEx={() => setClickImageEx(true)}
          handleClickUpload={() => setClickUploadImage(true)}
          previewImg={form.image}
          readOnly={isDetailMode}
        />

        <p
          className="bold-text"
          style={{ marginTop: "40px", marginBottom: "8px" }}
        >
          옵션 선택
        </p>

        <OptionSectionBox>
          <div className="hrz-wrapper">
            <p className="medium-text">대회 기간*</p>
            <div style={{ marginLeft: "auto", display: "flex" }}>
              <IconInfo fill="#888888" />
              <p
                style={{
                  fontSize: "15px",
                  color: "#888888",
                  marginLeft: "4px",
                }}
              >
                대회 시작일 5일 전부터 '진행예정' 화면 확인 가능
              </p>
            </div>
          </div>

          <div style={{ marginTop: "18px" }}>
            <DepartmentBattleDateOption
              startTitle="시작일"
              endTitle="종료일"
              width="100%"
              gap="16px"
              startDate={form.startDate}
              endDate={form.endDate}
              setStartDate={(value) =>
                setForm((prev) => ({ ...prev, startDate: value }))
              }
              setEndDate={(value) =>
                setForm((prev) => ({ ...prev, endDate: value }))
              }
              navigate={navigate}
              excludeSeasonDepartmentId={loadedEntry?.seasonDepartmentId ?? id}
              readOnly={isDetailMode}
            />
          </div>
        </OptionSectionBox>

        <OptionSectionBox style={{ marginTop: "8px" }}>
          <p className="medium-text" style={{ marginBottom: "14px" }}>
            진행 국가*
          </p>
          <RadioButton
            option1="국내"
            option2="해외"
            optionChange={(value) =>
              setForm((prev) => ({ ...prev, nation: value }))
            }
            selectedOption={form.nation}
            disabled={isDetailMode}
          />
        </OptionSectionBox>

        <p className="bold-text" style={{ marginTop: "40px" }}>
          내용 작성*
        </p>
        <InputTitle
          title={form.title}
          setTitle={(value) => setForm((prev) => ({ ...prev, title: value }))}
          placeholder="제목을 입력해주세요"
          readOnly={isDetailMode}
        />
        <InputContent
          content={form.description}
          setContent={(value) =>
            setForm((prev) => ({ ...prev, description: value }))
          }
          placeholder="상세 정보를 입력해주세요"
          maxLength={10000}
          readOnly={isDetailMode}
        />

        <p className="bold-text" style={{ marginTop: "40px" }}>
          목표 달성률 설정*
        </p>

        <TargetRateGrid>
          {[110, 120, 130, 140, 150].map((rate) => (
            <TargetRateButton
              key={rate}
              $active={form.targetRate === rate}
              $readOnly={isDetailMode}
              type="button"
              disabled={isDetailMode}
              onClick={() => {
                if (!isDetailMode) {
                  setForm((prev) => ({ ...prev, targetRate: rate }));
                }
              }}
            >
              {rate}%
            </TargetRateButton>
          ))}
        </TargetRateGrid>

        <HelperText>
          *목표 달성률은 이벤트 시작일 기준 전월의 부서 평균 걸음수를 기준으로 합니다.
        </HelperText>
        <HelperText>
          *전체 부서가 아닌 각 부서가 달성해야 하는 목표 달성률입니다.
        </HelperText>
        <HelperText>
          *현재 페이지에서 설정한 목표 달성률을 기준으로, '부서별 대항전' 탭에서
          부서별 달성률이 실시간 집계되어 랭킹에 반영됩니다.
        </HelperText>

        <p className="bold-text" style={{ marginTop: "32px" }}>
          전월 걸음 데이터가 없는 부서일 경우*
        </p>
        <HelperText>
          이벤트 시작일 이후 신규 가입한 부서 또는 전월에 걸음 데이터가 없는
          부서에 적용됩니다.
        </HelperText>

        <OptionSectionBox style={{ marginTop: "14px", boxShadow: "inset 0 0 0 1px #e4e4e4" }}>
          <NoDataOptionItem
            $active={form.noDataStepType === NO_DATA_STEP_TYPE.COMPANY_AVG}
          >
            <NoDataOptionHeader
              onClick={() => {
                if (!isDetailMode) {
                  setForm((prev) => ({
                    ...prev,
                    noDataStepType: NO_DATA_STEP_TYPE.COMPANY_AVG,
                  }));
                }
              }}
            >
              <NoDataOptionRadioIcon>
                {form.noDataStepType === NO_DATA_STEP_TYPE.COMPANY_AVG ? (
                  <RadioButtonActive />
                ) : (
                  <RadioButtonDefault />
                )}
              </NoDataOptionRadioIcon>
              <div>
                <p className="medium-text">전사 평균 적용</p>
                <HelperText style={{ marginTop: "6px" }}>
                  이벤트 시작일 기준 전월의 전사 전체 직원 평균 걸음수를 해당
                  부서의 기준값으로 자동 적용합니다.
                </HelperText>
              </div>
            </NoDataOptionHeader>
            {form.noDataStepType === NO_DATA_STEP_TYPE.COMPANY_AVG && (
              <InlineInfoBox>
                <IconInfo fill="#1167ed" />
                <span>
                  이벤트 등록 후 자동 산정된 전사 평균에 목표 달성률{" "}
                  <strong>{form.targetRate ?? "-"}%</strong>를 곱해 목표
                  걸음수가 산정됩니다.
                </span>
              </InlineInfoBox>
            )}
          </NoDataOptionItem>

          <NoDataDivider />

          <NoDataOptionItem
            $active={form.noDataStepType === NO_DATA_STEP_TYPE.MANUAL}
          >
            <NoDataOptionHeader
              onClick={() => {
                if (!isDetailMode) {
                  setForm((prev) => ({
                    ...prev,
                    noDataStepType: NO_DATA_STEP_TYPE.MANUAL,
                  }));
                }
              }}
            >
              <NoDataOptionRadioIcon>
                {form.noDataStepType === NO_DATA_STEP_TYPE.MANUAL ? (
                  <RadioButtonActive />
                ) : (
                  <RadioButtonDefault />
                )}
              </NoDataOptionRadioIcon>
              <div>
                <p className="medium-text">직접 입력</p>
                <HelperText style={{ marginTop: "6px" }}>
                  기준 걸음수를 직접 설정합니다. 전월 데이터가 없는 모든 부서에
                  동일하게 적용됩니다.
                </HelperText>
              </div>
            </NoDataOptionHeader>
            {form.noDataStepType === NO_DATA_STEP_TYPE.MANUAL && (
              <>
                <ManualInputRow $active={Boolean(form.noDataStepValue)}>
                  <ManualInput
                    type="text"
                    inputMode="numeric"
                    placeholder="ex) 8,000"
                    value={form.noDataStepValue}
                    onChange={(event) => {
                      if (isDetailMode) return;

                      const numbersOnly = event.target.value.replace(/[^0-9]/g, "");
                      setForm((prev) => ({
                        ...prev,
                        noDataStepValue: numbersOnly,
                      }));
                    }}
                    readOnly={isDetailMode}
                  />
                  <ManualInputUnit>걸음/일</ManualInputUnit>
                </ManualInputRow>
                <InlineInfoBox>
                  <IconInfo fill="#1167ed" />
                  <span>
                    입력한 기준값에 목표 달성률{" "}
                    <strong>{form.targetRate ?? "-"}%</strong>를 곱해 목표
                    걸음수가 산정됩니다.
                    {form.noDataStepValue &&
                    Number.isInteger(Number(form.noDataStepValue)) &&
                    Number(form.noDataStepValue) > 0 &&
                    Number.isInteger(Number(form.targetRate)) &&
                    Number(form.targetRate) > 0
                      ? (
                        <>
                          {" -> 목표: "}
                          <strong>
                            {Math.round(
                              Number(form.noDataStepValue) *
                                ((form.targetRate ?? 0) / 100)
                            ).toLocaleString()}{" "}
                            걸음/일
                          </strong>
                        </>
                      )
                      : (
                        <>
                          {" -> 목표: "}
                          <strong>- 걸음/일</strong>
                        </>
                      )}
                  </span>
                </InlineInfoBox>
              </>
            )}
          </NoDataOptionItem>
        </OptionSectionBox>

        <div style={{ display: "flex", marginTop: "30px" }}>
          <div
            className="cancelBtn center"
            style={{ marginLeft: "auto" }}
            onClick={() => navigate("/departmentBattle")}
          >
            {isDetailMode ? "목록" : "취소"}
          </div>
          {!isDetailMode && (
            <div
              className="applyBtn center"
              style={{ marginLeft: "16px" }}
              onClick={() => setClickApply(true)}
            >
              {isEditing ? "수정" : "등록"}
            </div>
          )}
        </div>
      </ApplyBox>
    </div>
  );
};

const ApplyBox = styled.div`
  width: 760px;
  height: auto;
  border-radius: 20px;
  margin-left: 413px;
  margin-top: 30px;
  margin-bottom: 30px;
  padding: 30px;
  background-color: #ffffff;
`;

const VisibilityBox = styled.div`
  margin-top: 28px;
  padding: 20px;
  border-radius: 10px;
  box-shadow: inset 0 0 0 1px #e4e4e4;
`;

const VisibilityContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const VisibilityHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const OptionSectionBox = styled.div`
  width: auto;
  height: auto;
  padding: 20px;
  border-radius: 10px;
  box-shadow: inset 0 0 0 1px #e4e4e4;
`;

const TargetRateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  margin-top: 14px;
`;

const TargetRateButton = styled.button`
  height: 56px;
  border: none;
  border-radius: 10px;
  box-shadow: inset 0 0 0 1px
    ${(props) => (props.$active ? "#1167ed" : "#e4e4e4")};
  background-color: ${(props) => (props.$active ? "#1167ed" : "#ffffff")};
  color: ${(props) => (props.$active ? "#ffffff" : "#888888")};
  font-family: "notoSans-medium";
  cursor: ${(props) => (props.$readOnly ? "default" : "pointer")};
  transition: background-color 0.2s ease, color 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    box-shadow: inset 0 0 0 1px
      ${(props) => (props.$readOnly ? (props.$active ? "#1167ed" : "#e4e4e4") : "#1167ed")};
    background-color: ${(props) =>
      props.$readOnly ? (props.$active ? "#1167ed" : "#ffffff") : "#1167ed"};
    color: ${(props) =>
      props.$readOnly ? (props.$active ? "#ffffff" : "#888888") : "#ffffff"};
  }
`;

const HelperText = styled.p`
  margin-top: 8px;
  font-size: 13px;
  color: #888888;
  line-height: 1.5;
`;

const NoDataOptionItem = styled.div`
  padding: 2px 0;
`;

const NoDataOptionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
`;

const NoDataOptionRadioIcon = styled.div`
  margin-top: 4px;
  flex-shrink: 0;
`;

const NoDataDivider = styled.div`
  height: 1px;
  background-color: #e4e4e4;
  margin: 18px 0;
`;

const InlineInfoBox = styled.div`
  margin-top: 14px;
  margin-left: 30px;
  padding: 10px 14px;
  border-radius: 10px;
  background-color: #f5f8ff;
  color: #1167ed;
  font-size: 14px;
  line-height: 1.5;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  > span {
    font-size: 14px;
  }

  > span strong {
    font-family: "notoSans-bold";
  }
`;

const ManualInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  height: 60px;
  margin-top: 18px;
  margin-left: 30px;
  box-shadow: ${({ $active }) =>
    $active ? "inset 0 -2px 0 #1167ed" : "inset 0 -2px 0 #b0b0b0"};
`;

const ManualInput = styled.input`
  flex: 1;
  height: 44px;
  border: none;
  font-size: 20px;
  color: #111111;
  padding: 0 4px;
  outline: none;
  background-color: transparent;

  &::placeholder {
    font-size: 20px;
    color: #b7b7b7;
  }
`;

const ManualInputUnit = styled.span`
  flex-shrink: 0;
  font-family: "notoSans-medium";
  font-size: 16px;
  color: #888888;
`;

const DepartmentBattleApply = () => {
  return <Layout page={<DepartmentBattleApplyContent />} />;
};

export default DepartmentBattleApply;
